import React, { useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { Printer, Lightbulb, Loader2 } from "lucide-react";
import { fetchItems, fetchItemDescription } from '../../../../store/itemSlice';

// Custom loading skeleton component
const LoadingSkeleton = () => (
  <div className="animate-pulse">
    <div className="flex justify-between items-center mb-4">
      <div className="h-8 w-32 bg-gray-200 rounded"></div>
      <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
    </div>
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="grid grid-cols-8 gap-4">
          <div className="h-8 bg-gray-200 rounded col-span-1"></div>
          <div className="h-8 bg-gray-200 rounded col-span-2"></div>
          <div className="h-8 bg-gray-200 rounded col-span-1"></div>
          <div className="h-8 bg-gray-200 rounded col-span-1"></div>
          <div className="h-8 bg-gray-200 rounded col-span-1"></div>
          <div className="h-8 bg-gray-200 rounded col-span-1"></div>
          <div className="h-8 bg-gray-200 rounded col-span-1"></div>
        </div>
      ))}
    </div>
  </div>
);

// Memoized Description Cell component
const DescriptionCell = React.memo(({ itemId }) => {
  const description = useSelector(state => state.items.descriptions[itemId]);

  return (
    <div className="relative group">
      <Lightbulb
        size={18}
        className={`transition-colors duration-200 ${
          description ? "text-yellow-500" : "text-gray-400"
        }`}
      />
      {description && (
        <div className="invisible group-hover:visible absolute z-10 w-64 p-2 mt-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg left-1/2 transform -translate-x-1/2">
          {description}
        </div>
      )}
    </div>
  );
});



const ItemsTable = React.memo(({
  indentID,
  isDraft,
  onPrint,
  indentStatus = { l1: 0, l2: 0, po: 0, ph: 0, so: 0 },
}) => {
  const dispatch = useDispatch();
  const items = useSelector(state => state.items.items);
  const status = useSelector(state => state.items.status);
  const error = useSelector(state => state.items.error);

  useEffect(() => {
    if (indentID) {
      dispatch(fetchItems(indentID)).then(action => {
        const items = action.payload; // Ensure `fetchItems` returns items in the payload
        items.forEach(item => {
          if (!item.description) {
            dispatch(fetchItemDescription({ itemId: item.itemID, indentID }));
          }
        });
      });
    }
  }, [dispatch, indentID]);
  

  // Memoized table headers
  const TableHeaders = useMemo(() => (
    <tr>
      <th className="px-4 py-3 text-left w-12">S.No</th>
      <th className="px-4 py-3 text-left">Item Name</th>
      <th className="px-4 py-3 text-center w-8"></th>
      <th className="px-4 py-3 text-left">AU</th>
      <th className="px-4 py-3 text-center w-24">Quantity</th>
      <th className="px-4 py-3 text-right">Stock</th>
      <th className="px-4 py-3 text-right">Committed Date</th>
      <th className="px-4 py-3 text-right">Promised Days</th>
      {indentStatus.so === 1 && (
        <>
          <th className="px-4 py-3 text-right">Received Qty</th>
          <th className="px-4 py-3 text-right">
            {indentStatus.ph === 1 ? "SH Remarks" : "Difference"}
          </th>
        </>
      )}
    </tr>
  ), [indentStatus.so, indentStatus.ph]);

  if (status === 'loading') {
    return (
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
        <LoadingSkeleton />
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      <div className="flex justify-between items-center border-b pb-4 mb-4">
        <h2 className="text-xl font-bold text-gray-800">Invoice Items</h2>
        <button
          className="text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors duration-300"
          onClick={onPrint}
          title="Print Invoice"
        >
          <Printer size={20} />
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-600">
          <thead className="bg-gray-100 text-gray-700 uppercase">
            {TableHeaders}
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item, index) => (
                <tr
                  key={item.indentItemID}
                  className="border-b hover:bg-gray-50 transition-colors duration-200"
                >
                  <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                  <td className="px-4 py-3 font-medium">{item.itemName}</td>
                  <td className="px-4 py-3 text-center">
                    <DescriptionCell itemId={item.itemID} indentID={indentID} />
                  </td>
                  <td className="px-4 py-3 font-medium">{item.unitname}</td>
                  <td className="px-4 py-3 text-center">{item.quantity}</td>
                  <td className="px-4 py-3 text-right">{item.stock}</td>
                  <td className="px-4 py-3 text-right">
                    {new Date(item.promisedDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">{item.normalDays}</td>
                  {indentStatus.so === 1 && (
                    <>
                      <td className="px-4 py-3 text-right">
                        {item.quantityReceived || 0}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {indentStatus.ph === 1
                          ? `FC ${item.difference}, ${item.shRemarks || ''}`
                          : item.difference || 0}
                      </td>
                    </>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={indentStatus.so === 1 ? 10 : 8}
                  className="text-center text-gray-500 py-6"
                >
                  No items added to the invoice
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default ItemsTable;